import React, { useEffect, useMemo, useState } from 'react';
import ActionPriorityBadge from './ActionPriorityBadge';
import ConfidenceSampleBadge from './ConfidenceSampleBadge';
import { API_BASE_URL } from '../../../config';
import { ADMIN_ROLE_KEYS, ADMIN_ROLE_LABELS_TR } from '../../../constants/AdminRoleMap';

function toText(value, fallback = '') {
  return String(value ?? fallback).trim();
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return [value];
}

function normalizeAction(item) {
  if (typeof item === 'string') {
    return {
      actionKey: '',
      title: item,
      reason: '',
      doNotApplyWhen: '',
      expectedImpact: '',
      affectedSegment: '',
      score: 0,
      enabled: true,
      lockReason: '',
      impacts: [],
      baselineExpected: null,
      context: null,
      status: 'uygulanmadi',
      resultDelta: '',
      updatedAt: '',
      ownerUserId: null,
      dueAt: null,
      priorityLevel: 'medium',
      notes: '',
      revenueImpact: 'medium',
      riskLevel: 'medium',
      urgency: 'backlog',
      overdue: false,
      whyThisAction: []
    };
  }

  if (!item || typeof item !== 'object') {
    return {
      actionKey: '',
      title: '',
      reason: '',
      doNotApplyWhen: '',
      expectedImpact: '',
      affectedSegment: '',
      score: 0,
      enabled: true,
      lockReason: '',
      impacts: [],
      baselineExpected: null,
      context: null,
      status: 'uygulanmadi',
      resultDelta: '',
      updatedAt: '',
      ownerUserId: null,
      dueAt: null,
      priorityLevel: 'medium',
      notes: '',
      revenueImpact: 'medium',
      riskLevel: 'medium',
      urgency: 'backlog',
      overdue: false,
      whyThisAction: []
    };
  }

  return {
    actionKey: toText(item.actionKey || item.aksiyonAnahtari),
    title: toText(item.title || item.aksiyon || item.message),
    reason: toText(item.reason || item.neden),
    doNotApplyWhen: toText(item.doNotApplyWhen || item.neZamanYapma),
    expectedImpact: toText(item.expectedImpact || item.beklenenEtki),
    affectedSegment: toText(item.affectedSegment || item.segmentEtkisi),
    score: Number(item.score || item.aksiyonSkoru || 0),
    enabled: item.enabled !== false,
    lockReason: toText(item.lockReason || item.kilitNedeni),
    impacts: toArray(item.impacts || item.etkiTurleri).filter(Boolean),
    baselineExpected: item.baselineExpected || item.mevcutBeklenen || null,
    context: item.context || item.baglam || null,
    status: toText(item.status || item.durum || 'uygulanmadi'),
    resultDelta: toText(item.resultDelta || item.sonucDegisimi),
    updatedAt: toText(item.updatedAt || item.guncellemeZamani),
    ownerUserId: Number(item.ownerUserId || 0) || null,
    dueAt: item.dueAt ? String(item.dueAt) : null,
    priorityLevel: toText(item.priority || item.priorityLevel || 'medium').toLowerCase(),
    notes: toText(item.notes || ''),
    revenueImpact: toText(item.revenueImpact || item.revenue_impact || 'medium').toLowerCase(),
    riskLevel: toText(item.riskLevel || item.risk_level || 'medium').toLowerCase(),
    urgency: toText(item.urgency || 'backlog').toLowerCase(),
    overdue: Boolean(item.overdue),
    whyThisAction: toArray(item.whyThisAction || item.nedenBuAksiyon).filter(Boolean)
  };
}

const AKSIYON_DURUMLARI = ['uygulanmadi', 'testte', 'canli', 'iptal'];
const IMPACT_LEVELS = ['high', 'medium', 'low'];
const URGENCY_LEVELS = ['today', 'this_week', 'backlog'];

function adminHeaders() {
  const token = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchSuperAdminAssignees() {
  try {
    const query = new URLSearchParams();
    query.set('roleKeys', `${ADMIN_ROLE_KEYS.SUPER_ADMIN},${ADMIN_ROLE_KEYS.OPS_ADMIN}`);
    const res = await fetch(`${API_BASE_URL}/api/admin/dashboard/decision-actions/assignees?${query.toString()}`, {
      credentials: 'include',
      headers: adminHeaders()
    });
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json?.assignees) ? json.assignees : [];
  } catch (_) {
    return [];
  }
}

function normalizeStatus(value) {
  const text = String(value || '').toLocaleLowerCase('tr-TR').trim();
  if (text === 'uygulanmadı') return 'uygulanmadi';
  if (AKSIYON_DURUMLARI.includes(text)) return text;
  return 'uygulanmadi';
}

function statusLabel(value) {
  const v = normalizeStatus(value);
  if (v === 'testte') return 'Testte';
  if (v === 'canli') return 'Canlı';
  if (v === 'iptal') return 'İptal';
  return 'Uygulanmadı';
}

function priorityLabel(value) {
  const v = String(value || 'medium').toLowerCase();
  if (v === 'high') return 'Yüksek';
  if (v === 'low') return 'Düşük';
  return 'Orta';
}

function impactLabel(value) {
  const v = toText(value || 'medium').toLowerCase();
  if (v === 'high') return 'Yüksek';
  if (v === 'low') return 'Düşük';
  return 'Orta';
}

function urgencyLabel(value) {
  const v = toText(value || 'backlog').toLowerCase();
  if (v === 'today') return 'Bugün';
  if (v === 'this_week') return 'Bu hafta';
  return 'Backlog';
}

function impactBadgeClass(value) {
  const v = toText(value).toLowerCase();
  if (v === 'high') return 'border-rose-300 bg-rose-50 text-rose-700 dark:border-rose-700 dark:bg-rose-900/20 dark:text-rose-300';
  if (v === 'medium') return 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300';
  return 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300';
}

function effectConfidenceLabel(value) {
  const v = toText(value || 'low').toLowerCase();
  if (v === 'high') return 'Yüksek';
  if (v === 'medium') return 'Orta';
  return 'Düşük';
}

function dateInputValue(isoDate) {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function normalizeAnomaly(item) {
  if (typeof item === 'string') {
    return {
      title: item,
      meaning: '',
      risk: '',
      recommendation: ''
    };
  }
  if (!item || typeof item !== 'object') {
    return { title: '', meaning: '', risk: '', recommendation: '' };
  }
  return {
    title: toText(item.title || item.mesaj),
    meaning: toText(item.meaning || item.anlami),
    risk: toText(item.risk || item.riski),
    recommendation: toText(item.recommendation || item.onerilenKontrol)
  };
}

function confidenceSentence(confidence, priority, sampleSize) {
  const c = toText(confidence, 'DÜŞÜK').toLocaleUpperCase('tr-TR');
  const pRaw = toText(priority, 'LOW').toUpperCase();
  const p = pRaw === 'HIGH' ? 'YÜKSEK' : pRaw === 'MEDIUM' ? 'ORTA' : 'DÜŞÜK';
  const sample = Number(sampleSize || 0).toLocaleString('tr-TR');

  if (c === 'YÜKSEK') {
    return `Güçlü sinyal: örneklem ${sample}. Öncelik ${p}; eylem adayı olarak değerlendirilebilir.`;
  }
  if (c === 'ORTA') {
    return `Orta güven: örneklem ${sample}. Öneriler kontrollü test ile uygulanmalı.`;
  }
  return `Erken sinyal: örneklem ${sample}. Veri yetersizse eylem yerine izleme tercih edilmeli.`;
}

function renderImpactLines(action) {
  if (action.impacts.length > 0) {
    return (
      <ul className="mt-1 space-y-0.5 text-xs">
        {action.impacts.map((impact, idx) => (
          <li key={`impact-${idx}`}>• {impact}</li>
        ))}
      </ul>
    );
  }

  if (!action.expectedImpact) return null;
  return <p className="mt-1 text-xs">{action.expectedImpact}</p>;
}

function renderBaselineExpected(action) {
  const be = action.baselineExpected;
  if (!be || typeof be !== 'object') return null;

  const metric = toText(be.metric || be.metrik, 'Metrik');
  const baseline = toText(be.baseline || be.mevcut);
  const expected = toText(be.expected || be.beklenen);

  if (!baseline && !expected) return null;

  return (
    <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
      <span className="font-semibold">{metric}:</span> Şu an {baseline || '-'} • Beklenen {expected || '-'}
    </p>
  );
}

function renderContext(action) {
  const ctx = action.context;
  if (!ctx || typeof ctx !== 'object') return null;

  const parts = [];
  if (ctx.orneklem != null) parts.push(`n: ${Number(ctx.orneklem || 0).toLocaleString('tr-TR')}`);
  if (ctx.segment) parts.push(`Segment: ${ctx.segment}`);
  if (ctx.yemekTipi) parts.push(`yemek tipi: ${ctx.yemekTipi}`);
  if (ctx.pencere) parts.push(`Tarih aralığı: ${ctx.pencere}`);

  if (parts.length === 0) return null;
  return <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">{parts.join(' | ')}</p>;
}

function renderWhyThisAction(action) {
  if (!action.whyThisAction.length) return null;
  return (
    <div className="mt-2 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 dark:border-indigo-700 dark:bg-indigo-900/20">
      <p className="text-[11px] font-semibold text-indigo-700 dark:text-indigo-300">Bu öneri şu sinyallere dayanır</p>
      <ul className="mt-1 space-y-0.5 text-xs text-indigo-800 dark:text-indigo-200">
        {action.whyThisAction.map((row, idx) => (
          <li key={`why-${idx}`}>• {row}</li>
        ))}
      </ul>
    </div>
  );
}

export default function DecisionEngineBlock({
  narrative = '',
  keyTakeaway = '',
  actions = [],
  priority = 'LOW',
  confidence = 'DÜŞÜK',
  sampleSize = 0,
  anomalies = [],
  expectedOutcome = null,
  actionLock = false,
  actionLockReason = '',
  actionStateScope = null
}) {
  const [durumHaritasi, setDurumHaritasi] = useState({});
  const [formHaritasi, setFormHaritasi] = useState({});
  const [durumKayitYukleniyor, setDurumKayitYukleniyor] = useState(false);
  const [effectSummary, setEffectSummary] = useState(null);
  const [effectYukleniyor, setEffectYukleniyor] = useState(false);
  const [assignees, setAssignees] = useState([]);

  const actionItems = useMemo(() => {
    return (Array.isArray(actions) ? actions : [])
      .map(normalizeAction)
      .filter((x) => x.title)
      .map((x, idx) => ({ ...x, actionKey: x.actionKey || `aksiyon-${idx}`, _idx: idx }))
      .map((x) => {
        const kayit = durumHaritasi[x.actionKey];
        if (!kayit) return x;
        return {
          ...x,
          status: kayit.status || x.status,
          resultDelta: kayit.resultDelta ?? x.resultDelta,
          updatedAt: kayit.updatedAt || x.updatedAt,
          ownerUserId: kayit.ownerUserId ?? x.ownerUserId,
          dueAt: kayit.dueAt ?? x.dueAt,
          priorityLevel: kayit.priorityLevel || x.priorityLevel,
          notes: kayit.notes ?? x.notes,
          revenueImpact: kayit.revenueImpact || x.revenueImpact,
          riskLevel: kayit.riskLevel || x.riskLevel,
          urgency: kayit.urgency || x.urgency,
          overdue: typeof kayit.overdue === 'boolean' ? kayit.overdue : x.overdue
        };
      })
      .sort((a, b) => (b.score - a.score) || (a._idx - b._idx));
  }, [actions, durumHaritasi]);

  const primaryAction = actionItems[0] || null;
  const alternativeActions = actionItems.slice(1, 3);

  const anomalyItems = (Array.isArray(anomalies) ? anomalies : [])
    .map(normalizeAnomaly)
    .filter((x) => x.title);

  const takeaway = toText(keyTakeaway || narrative);
  const confidenceText = confidenceSentence(confidence, priority, sampleSize);
  const lockMessage = toText(actionLockReason || 'Bu aksiyon şu an uygulanamaz: veri güveni düşük veya akış tutarsız.');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const admins = await fetchSuperAdminAssignees();
        if (mounted) setAssignees(admins);
      } catch (_) {
        if (mounted) setAssignees([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!actionStateScope?.drawerKey || !actionStateScope?.entityKey) {
        if (mounted) setDurumHaritasi({});
        return;
      }
      try {
        setDurumKayitYukleniyor(true);
        const query = new URLSearchParams({
          drawerKey: String(actionStateScope.drawerKey),
          entityKey: String(actionStateScope.entityKey)
        });
        const res = await fetch(`${API_BASE_URL}/api/admin/dashboard/decision-actions?${query.toString()}`, {
          credentials: 'include',
          headers: adminHeaders()
        });
        if (!res.ok) return;
        const json = await res.json();
        if (!mounted) return;
        const map = {};
        for (const item of (Array.isArray(json?.items) ? json.items : [])) {
          const key = String(item?.actionKey || '').trim();
          if (!key) continue;
          map[key] = {
            status: normalizeStatus(item.status),
            resultDelta: toText(item.resultDelta || ''),
            updatedAt: toText(item.updatedAt || ''),
            ownerUserId: Number(item.ownerUserId || 0) || null,
            dueAt: item.dueAt ? String(item.dueAt) : null,
            priorityLevel: toText(item.priority || 'medium').toLowerCase(),
            notes: toText(item.notes || ''),
            revenueImpact: toText(item.revenueImpact || 'medium').toLowerCase(),
            riskLevel: toText(item.riskLevel || 'medium').toLowerCase(),
            urgency: toText(item.urgency || 'backlog').toLowerCase(),
            overdue: Boolean(item.overdue)
          };
        }
        setDurumHaritasi(map);
      } catch (_) {
        if (mounted) setDurumHaritasi({});
      } finally {
        if (mounted) setDurumKayitYukleniyor(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [actionStateScope?.drawerKey, actionStateScope?.entityKey]);

  useEffect(() => {
    setFormHaritasi((prev) => {
      const next = { ...prev };
      for (const action of actionItems) {
        if (!next[action.actionKey]) {
          next[action.actionKey] = {
            ownerUserId: action.ownerUserId ? String(action.ownerUserId) : '',
            dueAt: dateInputValue(action.dueAt),
            priorityLevel: action.priorityLevel || 'medium',
            notes: action.notes || '',
            revenueImpact: action.revenueImpact || 'medium',
            riskLevel: action.riskLevel || 'medium',
            urgency: action.urgency || 'backlog'
          };
        }
      }
      return next;
    });
  }, [actionItems]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!actionStateScope?.drawerKey || !actionStateScope?.entityKey || !primaryAction?.actionKey) {
        if (mounted) setEffectSummary(null);
        return;
      }
      try {
        setEffectYukleniyor(true);
        const query = new URLSearchParams({
          drawerKey: String(actionStateScope.drawerKey),
          entityKey: String(actionStateScope.entityKey),
          actionKey: String(primaryAction.actionKey),
          windowDays: '7'
        });
        const res = await fetch(`${API_BASE_URL}/api/admin/dashboard/decision-actions/effect?${query.toString()}`, {
          credentials: 'include',
          headers: adminHeaders()
        });
        if (!res.ok) {
          if (mounted) setEffectSummary(null);
          return;
        }
        const json = await res.json();
        if (!mounted) return;
        setEffectSummary(json?.effect || null);
      } catch (_) {
        if (mounted) setEffectSummary(null);
      } finally {
        if (mounted) setEffectYukleniyor(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [actionStateScope?.drawerKey, actionStateScope?.entityKey, primaryAction?.actionKey, primaryAction?.status, primaryAction?.updatedAt]);

  const formDegeriniGuncelle = (actionKey, patch = {}) => {
    setFormHaritasi((prev) => {
      const current = prev[actionKey] || { ownerUserId: '', dueAt: '', priorityLevel: 'medium', notes: '', revenueImpact: 'medium', riskLevel: 'medium', urgency: 'backlog' };
      return {
        ...prev,
        [actionKey]: { ...current, ...patch }
      };
    });
  };

  const atamaKaydet = async (action) => {
    if (!actionStateScope?.drawerKey || !actionStateScope?.entityKey || !action?.actionKey) return;
    const form = formHaritasi[action.actionKey] || {};
    const ownerParsed = Number(form.ownerUserId || 0);
    const payload = {
      drawerKey: actionStateScope.drawerKey,
      entityKey: actionStateScope.entityKey,
      actionKey: action.actionKey,
      ownerUserId: ownerParsed > 0 ? ownerParsed : null,
      dueAt: form.dueAt || null,
      priority: form.priorityLevel || 'medium',
      revenueImpact: form.revenueImpact || 'medium',
      riskLevel: form.riskLevel || 'medium',
      urgency: form.urgency || 'backlog',
      notes: toText(form.notes || '').slice(0, 500)
    };

    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/dashboard/decision-actions/assignment`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...adminHeaders() },
        body: JSON.stringify(payload)
      });
      if (!res.ok) return;
      const json = await res.json();
      const item = json?.item || null;
      if (!item) return;
      setDurumHaritasi((prev) => ({
        ...prev,
        [action.actionKey]: {
          status: normalizeStatus(item.status),
          resultDelta: toText(item.resultDelta || ''),
          updatedAt: toText(item.updatedAt || ''),
          ownerUserId: Number(item.ownerUserId || 0) || null,
          dueAt: item.dueAt ? String(item.dueAt) : null,
          priorityLevel: toText(item.priority || 'medium').toLowerCase(),
          notes: toText(item.notes || ''),
          revenueImpact: toText(item.revenueImpact || 'medium').toLowerCase(),
          riskLevel: toText(item.riskLevel || 'medium').toLowerCase(),
          urgency: toText(item.urgency || 'backlog').toLowerCase(),
          overdue: Boolean(item.overdue)
        }
      }));
    } catch (_) {
      // Sessiz bırakılır.
    }
  };

  const durumGuncelle = async (action, nextStatus) => {
    if (!actionStateScope?.drawerKey || !actionStateScope?.entityKey || !action?.actionKey) return;
    const status = normalizeStatus(nextStatus);
    const optimistic = {
      status,
      resultDelta: action.resultDelta || '',
      updatedAt: new Date().toLocaleDateString('tr-TR')
    };
    setDurumHaritasi((prev) => ({ ...prev, [action.actionKey]: optimistic }));

    try {
      const form = formHaritasi[action.actionKey] || {};
      const ownerParsed = Number(form.ownerUserId || 0);
      const res = await fetch(`${API_BASE_URL}/api/admin/dashboard/decision-actions/status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...adminHeaders() },
        body: JSON.stringify({
          drawerKey: actionStateScope.drawerKey,
          entityKey: actionStateScope.entityKey,
          actionKey: action.actionKey,
          status,
          resultDelta: action.resultDelta || '',
          ownerUserId: ownerParsed > 0 ? ownerParsed : null,
          dueAt: form.dueAt || null,
          priority: form.priorityLevel || 'medium',
          revenueImpact: form.revenueImpact || 'medium',
          riskLevel: form.riskLevel || 'medium',
          urgency: form.urgency || 'backlog',
          notes: toText(form.notes || '').slice(0, 500)
        })
      });
      if (!res.ok) return;
      const json = await res.json();
      const item = json?.item || null;
      if (!item) return;
      setDurumHaritasi((prev) => ({
        ...prev,
        [action.actionKey]: {
          status: normalizeStatus(item.status),
          resultDelta: toText(item.resultDelta || ''),
          updatedAt: toText(item.updatedAt || optimistic.updatedAt),
          ownerUserId: Number(item.ownerUserId || 0) || null,
          dueAt: item.dueAt ? String(item.dueAt) : null,
          priorityLevel: toText(item.priority || 'medium').toLowerCase(),
          notes: toText(item.notes || ''),
          revenueImpact: toText(item.revenueImpact || 'medium').toLowerCase(),
          riskLevel: toText(item.riskLevel || 'medium').toLowerCase(),
          urgency: toText(item.urgency || 'backlog').toLowerCase(),
          overdue: Boolean(item.overdue)
        }
      }));
    } catch (_) {
      // Sessiz bırakılır, optimistic görünüm korunur.
    }
  };

  return (
    <section className="space-y-3 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900/60">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Karar Motoru Katmanı</p>
        <div className="flex flex-wrap items-center gap-2">
          <ActionPriorityBadge priority={priority} />
          <ConfidenceSampleBadge confidence={confidence} sampleSize={sampleSize} />
        </div>
      </div>

      {primaryAction ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-2 dark:border-emerald-700 dark:bg-emerald-900/20">
          <p className="text-[11px] font-bold text-emerald-700 dark:text-emerald-300">Ana aksiyon</p>
          <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">{primaryAction.title}</p>
          <p className="mt-1 text-xs text-emerald-800 dark:text-emerald-200">Aksiyon skoru: {primaryAction.score.toFixed(2)}</p>

          {primaryAction.reason ? <p className="mt-1 text-xs"><span className="font-semibold">Neden:</span> {primaryAction.reason}</p> : null}

          <div className="mt-1 text-xs">
            <span className="font-semibold">Etki:</span>
            {renderImpactLines(primaryAction)}
          </div>

          {renderBaselineExpected(primaryAction)}
          {renderContext(primaryAction)}
          {renderWhyThisAction(primaryAction)}

          <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
            <span className="font-semibold">Durum:</span> {statusLabel(primaryAction.status)}
            {primaryAction.resultDelta ? ` • Sonuç: ${primaryAction.resultDelta}` : ''}
            {primaryAction.updatedAt ? ` • Güncelleme: ${primaryAction.updatedAt}` : ''}
            {primaryAction.overdue ? ' • Gecikmiş' : ''}
          </p>
          <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
            <span className={`rounded border px-1.5 py-0.5 font-semibold ${impactBadgeClass(primaryAction.urgency === 'today' ? 'high' : primaryAction.urgency === 'this_week' ? 'medium' : 'low')}`}>
              Aciliyet: {urgencyLabel(primaryAction.urgency)}
            </span>
            <span className={`rounded border px-1.5 py-0.5 font-semibold ${impactBadgeClass(primaryAction.revenueImpact)}`}>
              Gelir etkisi: {impactLabel(primaryAction.revenueImpact)}
            </span>
            <span className={`rounded border px-1.5 py-0.5 font-semibold ${impactBadgeClass(primaryAction.riskLevel)}`}>
              Risk: {impactLabel(primaryAction.riskLevel)}
            </span>
          </div>
          <div className="mt-2 rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800">
            <p className="font-semibold text-slate-700 dark:text-slate-200">Etki özeti (7 gün)</p>
            {effectYukleniyor ? (
              <p className="mt-1 text-slate-600 dark:text-slate-300">Hesaplanıyor...</p>
            ) : effectSummary?.ready ? (
              <>
                <p className="mt-1 text-slate-700 dark:text-slate-200">
                  Önce: %{Number(effectSummary?.baselineAcceptRate || 0).toFixed(2)} • Sonra: %{Number(effectSummary?.afterAcceptRate || 0).toFixed(2)} • Delta: %{Number(effectSummary?.delta || 0).toFixed(2)}
                </p>
                <p className="mt-1 text-slate-600 dark:text-slate-300">
                  Güven: {effectConfidenceLabel(effectSummary?.confidence)} • Canlıya alınma: {effectSummary?.actionLiveAt ? new Date(effectSummary.actionLiveAt).toLocaleDateString('tr-TR') : '-'}
                  {effectSummary?.after?.windowComplete ? '' : ' • Pencere devam ediyor'}
                </p>
              </>
            ) : (
              <p className="mt-1 text-slate-600 dark:text-slate-300">Etki ölçümü için aksiyonun canlı statüde olması gerekir.</p>
            )}
          </div>

          <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-4">
            <label className="text-xs">
              <span className="mb-0.5 block font-semibold">Sorumlu</span>
              <select
                value={formHaritasi[primaryAction.actionKey]?.ownerUserId || ''}
                onChange={(e) => formDegeriniGuncelle(primaryAction.actionKey, { ownerUserId: e.target.value })}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
              >
                <option value="">{ADMIN_ROLE_LABELS_TR[ADMIN_ROLE_KEYS.SUPER_ADMIN]} / {ADMIN_ROLE_LABELS_TR[ADMIN_ROLE_KEYS.OPS_ADMIN]} seç</option>
                {!assignees.length ? <option value="" disabled>Uygun sorumlu bulunamadı</option> : null}
                {assignees.map((admin) => (
                  <option key={`assignee-${admin.id}`} value={String(admin.id)}>
                    {admin.name ? `${admin.name} (#${admin.id})` : `${admin.email} (#${admin.id})`}
                  </option>
                ))}
                {formHaritasi[primaryAction.actionKey]?.ownerUserId && !assignees.find((x) => String(x.id) === String(formHaritasi[primaryAction.actionKey]?.ownerUserId)) ? (
                  <option value={String(formHaritasi[primaryAction.actionKey]?.ownerUserId)}>
                    #{String(formHaritasi[primaryAction.actionKey]?.ownerUserId)} (listede yok)
                  </option>
                ) : null}
              </select>
            </label>
            <label className="text-xs">
              <span className="mb-0.5 block font-semibold">Termin</span>
              <input
                type="date"
                value={formHaritasi[primaryAction.actionKey]?.dueAt || ''}
                onChange={(e) => formDegeriniGuncelle(primaryAction.actionKey, { dueAt: e.target.value })}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
              />
            </label>
            <label className="text-xs">
              <span className="mb-0.5 block font-semibold">Öncelik</span>
              <select
                value={formHaritasi[primaryAction.actionKey]?.priorityLevel || 'medium'}
                onChange={(e) => formDegeriniGuncelle(primaryAction.actionKey, { priorityLevel: e.target.value })}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
              >
                <option value="high">Yüksek</option>
                <option value="medium">Orta</option>
                <option value="low">Düşük</option>
              </select>
            </label>
            <button
              type="button"
              onClick={() => atamaKaydet(primaryAction)}
              disabled={durumKayitYukleniyor}
              className="self-end rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            >
              Atamayı Kaydet
            </button>
          </div>
          <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
            <label className="text-xs">
              <span className="mb-0.5 block font-semibold">Aciliyet</span>
              <select
                value={formHaritasi[primaryAction.actionKey]?.urgency || 'backlog'}
                onChange={(e) => formDegeriniGuncelle(primaryAction.actionKey, { urgency: e.target.value })}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
              >
                {URGENCY_LEVELS.map((v) => (
                  <option key={`urgency-${v}`} value={v}>{urgencyLabel(v)}</option>
                ))}
              </select>
            </label>
            <label className="text-xs">
              <span className="mb-0.5 block font-semibold">Gelir etkisi</span>
              <select
                value={formHaritasi[primaryAction.actionKey]?.revenueImpact || 'medium'}
                onChange={(e) => formDegeriniGuncelle(primaryAction.actionKey, { revenueImpact: e.target.value })}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
              >
                {IMPACT_LEVELS.map((v) => (
                  <option key={`revenue-${v}`} value={v}>{impactLabel(v)}</option>
                ))}
              </select>
            </label>
            <label className="text-xs">
              <span className="mb-0.5 block font-semibold">Risk seviyesi</span>
              <select
                value={formHaritasi[primaryAction.actionKey]?.riskLevel || 'medium'}
                onChange={(e) => formDegeriniGuncelle(primaryAction.actionKey, { riskLevel: e.target.value })}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1 dark:border-slate-700 dark:bg-slate-900"
              >
                {IMPACT_LEVELS.map((v) => (
                  <option key={`risk-${v}`} value={v}>{impactLabel(v)}</option>
                ))}
              </select>
            </label>
          </div>

          <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
            <span className="font-semibold">Öncelik:</span> {priorityLabel(formHaritasi[primaryAction.actionKey]?.priorityLevel || primaryAction.priorityLevel)}
          </p>

          <div className="mt-2 flex flex-wrap gap-1">
            {AKSIYON_DURUMLARI.map((durum) => (
              <button
                key={`${primaryAction.actionKey}-${durum}`}
                type="button"
                onClick={() => durumGuncelle(primaryAction, durum)}
                disabled={actionLock || !primaryAction.enabled || durumKayitYukleniyor}
                className={`rounded border px-2 py-1 text-[11px] font-semibold ${
                  normalizeStatus(primaryAction.status) === durum
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-indigo-400 dark:bg-indigo-900/30 dark:text-indigo-200'
                    : 'border-slate-300 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
                }`}
              >
                {statusLabel(durum)}
              </button>
            ))}
          </div>

          {(actionLock || !primaryAction.enabled) ? (
            <p className="mt-2 rounded-md border border-amber-300 bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
              🔒 {primaryAction.lockReason || lockMessage}
            </p>
          ) : null}

          {primaryAction.doNotApplyWhen ? (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
              <span className="font-semibold">Ne zaman yapma:</span> {primaryAction.doNotApplyWhen}
            </p>
          ) : null}
        </div>
      ) : null}

      {anomalyItems.length > 0 ? (
        <div>
          <p className="text-xs font-semibold text-amber-600 dark:text-amber-400">Kritik uyarı</p>
          <ul className="mt-2 space-y-2 text-sm text-amber-700 dark:text-amber-300">
            {anomalyItems.map((item, index) => (
              <li key={`anomaly-${index}`} className="rounded-md border border-amber-200 bg-amber-50 px-2 py-2 dark:border-amber-700 dark:bg-amber-900/20">
                <p className="font-semibold">{item.title}</p>
                {item.meaning ? <p className="mt-1 text-xs"><span className="font-semibold">Bu ne demek:</span> {item.meaning}</p> : null}
                {item.risk ? <p className="mt-1 text-xs"><span className="font-semibold">Neden kritik:</span> {item.risk}</p> : null}
                {item.recommendation ? <p className="mt-1 text-xs"><span className="font-semibold">Ne yapmalı:</span> {item.recommendation}</p> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {takeaway ? (
        <div className="rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 dark:border-indigo-700 dark:bg-indigo-900/20">
          <p className="text-[11px] font-bold text-indigo-700 dark:text-indigo-300">Ana çıkarım</p>
          <p className="text-sm text-indigo-900 dark:text-indigo-100">{takeaway}</p>
        </div>
      ) : null}

      {narrative ? (
        <p className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
          {narrative}
        </p>
      ) : null}

      <p className="text-xs text-slate-600 dark:text-slate-300">{confidenceText}</p>

      {alternativeActions.length > 0 ? (
        <details className="rounded-md border border-slate-200 bg-slate-50 px-2 py-2 dark:border-slate-700 dark:bg-slate-800">
          <summary className="cursor-pointer text-xs font-semibold text-slate-600 dark:text-slate-300">Alternatif öneriler</summary>
          <ul className="mt-2 space-y-2 text-sm text-slate-700 dark:text-slate-200">
            {alternativeActions.map((item, index) => (
              <li key={`alt-action-${index}`} className="rounded-md border border-slate-200 bg-white px-2 py-2 dark:border-slate-700 dark:bg-slate-900/60">
                <p className="font-semibold text-slate-900 dark:text-white">{item.title}</p>
                <p className="mt-1 text-xs">Aksiyon skoru: {item.score.toFixed(2)}</p>
                <p className="mt-1 text-xs"><span className="font-semibold">Durum:</span> {statusLabel(item.status)}</p>
                <p className="mt-1 text-xs">
                  <span className="font-semibold">Aciliyet:</span> {urgencyLabel(item.urgency)} • <span className="font-semibold">Gelir:</span> {impactLabel(item.revenueImpact)} • <span className="font-semibold">Risk:</span> {impactLabel(item.riskLevel)}
                </p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {AKSIYON_DURUMLARI.map((durum) => (
                    <button
                      key={`${item.actionKey}-${durum}`}
                      type="button"
                      onClick={() => durumGuncelle(item, durum)}
                      disabled={actionLock || !item.enabled || durumKayitYukleniyor}
                      className={`rounded border px-2 py-1 text-[10px] font-semibold ${
                        normalizeStatus(item.status) === durum
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-indigo-400 dark:bg-indigo-900/30 dark:text-indigo-200'
                          : 'border-slate-300 bg-white text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
                      }`}
                    >
                      {statusLabel(durum)}
                    </button>
                  ))}
                </div>
                {item.reason ? <p className="mt-1 text-xs"><span className="font-semibold">Neden:</span> {item.reason}</p> : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {expectedOutcome ? (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Beklenen etki: <span className="font-bold text-slate-700 dark:text-slate-200">{expectedOutcome}</span>
        </p>
      ) : null}
    </section>
  );
}
